import { useEffect, useState } from "react";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { useQuery } from "@rocicorp/zero/react";
import {
  useMakeUrl,
  navigate,
  Route,
  routes,
  Switch,
  useCurrentUrl,
  useRouteMatch,
} from "../../../lib/urls";
import { Button } from "../../Button";
import { ButtonLink } from "../../Button/ButtonLink";
import { IdpLockTooltip } from "../../IdpLockTooltip";
import { TabHeaderBar, Tabs } from "../../TabHeader";
import { TabLink } from "../../TabLink";
import { IdentityProvidersButton } from "../OidcProvidersSettings/IdentityProvidersButton";
import { ServiceAccountDetail } from "../ServiceAccountsSettings/ServiceAccountDetail";
import { ServiceAccountsSettings } from "../ServiceAccountsSettings";
import { Container, HeaderActions, HelpTooltip, SearchField } from "../shared";
import { MembersList } from "./MembersList";

export function MembersSettings() {
  const makeUrl = useMakeUrl();
  const [onMembers] = useRouteMatch(routes.settings.members.users);
  const [onServiceAccounts] = useRouteMatch(
    routes.settings.members.serviceAccounts
  );
  const canManageUsers = useHasAccess("tenant:users_manage");
  const canCreateServiceAccounts = useHasAccess(
    "tenant:service_account_create"
  );
  const canManageOidc = useHasAccess("tenant:oidc_providers_manage");
  const { locked: idpSyncLocked, reason: idpSyncReason } =
    useIdpSyncLock("userProvisioning");

  const [members, membersResult] = useQuery(queries.allUsers());
  const [serviceAccounts, serviceAccountsResult] = useQuery(
    queries.serviceAccounts()
  );
  const activeMemberCount = members.filter(
    (member) => member.deactivated !== true
  ).length;
  const memberCount =
    membersResult.type === "complete" && activeMemberCount > 0
      ? activeMemberCount
      : null;
  const serviceAccountCount =
    serviceAccountsResult.type === "complete" && serviceAccounts.length > 0
      ? serviceAccounts.length
      : null;

  const [showCreateServiceAccount, setShowCreateServiceAccount] =
    useState(false);

  // The search field writes `q` to whichever tab is active (navigate(null,…)
  // targets the current route); each list reads its own `q` to filter.
  const membersUrl = useCurrentUrl(routes.settings.members.users);
  const serviceAccountsUrl = useCurrentUrl(
    routes.settings.members.serviceAccounts
  );
  const search =
    (onServiceAccounts ? serviceAccountsUrl.q : membersUrl.q) ?? "";
  const setSearch = (next: string) =>
    navigate(null, { q: next }, { replace: true });

  return (
    <Container>
      <TabHeaderBar>
        <Tabs>
          <TabLink
            to={makeUrl(routes.settings.members.users)}
            active={onMembers}
            icon="id-card"
            label="Members"
            count={memberCount}
          />
          <TabLink
            to={makeUrl(routes.settings.members.serviceAccounts)}
            active={onServiceAccounts}
            icon="bot"
            label="Service accounts"
            count={serviceAccountCount}
          >
            <HelpTooltip content="Non-human identities — for scripts, CI jobs, and services. Each holds its own permissions and keys." />
          </TabLink>
        </Tabs>
        <HeaderActions>
          {onServiceAccounts && canManageOidc && <IdentityProvidersButton />}
          <SearchField
            placeholder={
              onServiceAccounts ? "Search service accounts…" : "Search members…"
            }
            value={search}
            onChange={setSearch}
          />
          {onServiceAccounts
            ? canCreateServiceAccounts && (
                <Button
                  size="l"
                  icon="plus"
                  onClick={() => setShowCreateServiceAccount(true)}
                />
              )
            : canManageUsers && (
                <IdpLockTooltip reason={idpSyncLocked ? idpSyncReason : null}>
                  {idpSyncLocked ? (
                    <Button size="l" icon="plus" disabled />
                  ) : (
                    <ButtonLink
                      size="l"
                      icon="plus"
                      to={makeUrl(routes.settings.members.users.invite)}
                    />
                  )}
                </IdpLockTooltip>
              )}
        </HeaderActions>
      </TabHeaderBar>
      <Switch>
        <Route path={routes.settings.members.users}>
          <MembersList />
        </Route>
        <Route path={routes.settings.members.serviceAccounts}>
          <ServiceAccountsSettings />
        </Route>
        <Route path={routes.settings.members}>
          <RedirectToUsers />
        </Route>
      </Switch>
      {showCreateServiceAccount && (
        <ServiceAccountDetail
          serviceAccountId={null}
          onClose={() => setShowCreateServiceAccount(false)}
        />
      )}
    </Container>
  );
}

function RedirectToUsers() {
  useEffect(() => {
    navigate(routes.settings.members.users, undefined, { replace: true });
  }, []);
  return null;
}
